import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Loader2, Menu, MoreHorizontal } from "lucide-react";
import StoryEmbed, { type StoryType } from "@/components/StoryEmbed";

/**
 * HeroDemo: 3-phase pipeline cycling through Torflash's functions:
 *   Phase 1: SPIELANKÜNDIGUNG (0–6s)
 *   Phase 2: TORALARM         (6–12s)
 *   Phase 3: ENDERGEBNIS      (12–18s)
 *
 * Left:   BFV-style mobile liveticker
 * Center: Revolver dial (3 segments rotating 120°)
 * Right:  Instagram Story (blue accents)
 */

type FunctionKey = "spiel" | "tor" | "ergebnis";
const FUNCTIONS: FunctionKey[] = ["spiel", "tor", "ergebnis"];

const STORY_FOR_PHASE: Record<FunctionKey, StoryType> = {
  spiel: "matchday",
  tor: "goal",
  ergebnis: "endergebnis",
};

const BFV_NAVY = "#1a2342";
const TF_BLUE = "#2563EB";


const REFERENCE_CLUB = "SV Beispielkirchen";
const REFERENCE_OPPONENT = "FC Musterstadt";
const REFERENCE_PLAYER_IMAGE = "https://api.torflash.de/tfassets/dcassets/cutouts/jubel-1.png";
const REFERENCE_CREST = "https://api.torflash.de/tfassets/crests/demo-svb.svg";
const REFERENCE_AWAY_CREST = "https://api.torflash.de/tfassets/crests/demo-fcm.svg";

export default function HeroDemo({ primaryColor = "#1B5EAB" }: { primaryColor?: string }) {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [subPhase, setSubPhase] = useState<"intro" | "action" | "hold">("intro");
  const [paused, setPaused] = useState(false);
  const [cycle, setCycle] = useState(0);
  const timers = useRef<number[]>([]);

  const active = FUNCTIONS[phaseIdx];

  const clearTimers = () => {
    timers.current.forEach((t) => window.clearTimeout(t));
    timers.current = [];
  };

  useEffect(() => {
    if (paused) return;
    clearTimers();
    setSubPhase("intro");
    const t = (fn: () => void, ms: number) =>
      timers.current.push(window.setTimeout(fn, ms));

    t(() => setSubPhase("action"), 1200);
    t(() => setSubPhase("hold"), 4200);
    t(() => {
      setPhaseIdx((p) => (p + 1) % FUNCTIONS.length);
      setCycle((c) => c + 1);
    }, 6000);

    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseIdx, paused]);

  const handleReplay = () => {
    setPaused(false);
    setPhaseIdx(0);
    setCycle((c) => c + 1);
  };

  return (
    <div
      className="relative mx-auto w-full max-w-[860px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => setPaused((p) => !p)}
    >
      <div className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-white px-3 pt-4 pb-5 shadow-2xl shadow-blue-200/40 ring-1 ring-slate-900/[0.03] sm:px-6">
        <p className="mb-4 text-center text-[11px] italic leading-snug text-slate-500 sm:text-xs">
          So funktioniert Torflash im Hintergrund (vereinfachte Illustration)
        </p>
        <div className="grid grid-cols-1 items-center gap-6 sm:grid-cols-[1fr_auto_1fr] sm:gap-4">
          {/* PANEL 1: Liveticker */}
          <div className="flex justify-center">
            <PhoneFrame>
              <Liveticker active={active} subPhase={subPhase} cycle={cycle} />
            </PhoneFrame>
          </div>

          {/* PANEL 2: Brain */}
          <BrainPanel activeIdx={phaseIdx} subPhase={subPhase} cycle={cycle} />

          {/* PANEL 3: Live Story-Embed (wechselt mit der Phase) */}
          <div className="flex justify-center">
            <PhoneFrame>
              <div className="flex h-full w-full items-center justify-center overflow-hidden bg-slate-900">
                <StoryEmbed
                  key={`${STORY_FOR_PHASE[active]}-${primaryColor}`}
                  type={STORY_FOR_PHASE[active]}
                  params={{
                    club: REFERENCE_CLUB,
                    opp: REFERENCE_OPPONENT,
                    league: "Regionalliga Bayern",
                    pc: primaryColor,
                    crest: REFERENCE_CREST,
                    crestAway: REFERENCE_AWAY_CREST,
                    scorer: "L. Beispiel",
                    playerImage: REFERENCE_PLAYER_IMAGE,
                    homeScore: STORY_FOR_PHASE[active] === "matchday" ? "0" : STORY_FOR_PHASE[active] === "goal" ? "2" : "3",
                    awayScore: STORY_FOR_PHASE[active] === "goal" || STORY_FOR_PHASE[active] === "endergebnis" ? "1" : "0",
                  }}
                  fill
                  className="h-full w-full"
                  title="Story-Vorschau"
                />
              </div>
            </PhoneFrame>
          </div>

        </div>

        <AnimatePresence>
          {paused && (
            <motion.button
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              onClick={(e) => { e.stopPropagation(); handleReplay(); }}
              className="absolute bottom-4 right-4 z-30 flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1.5 text-xs font-medium text-background shadow-md hover:opacity-90"
            >
              <Play className="h-3 w-3 fill-current" />
              Replay
            </motion.button>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ───────────────── Phone Frame ───────────────── */

function PhoneFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative w-full" style={{ maxWidth: 250 }}>
      <div className="relative rounded-[1.8rem] border-[7px] border-slate-900 bg-slate-900 shadow-xl">
        <div className="absolute left-1/2 top-1.5 z-20 h-1 w-12 -translate-x-1/2 rounded-full bg-slate-700" />
        <div className="relative overflow-hidden rounded-[1.25rem] bg-white" style={{ aspectRatio: "9/16" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

/* ───────────────── Liveticker ───────────────── */

function Liveticker({ active, subPhase, cycle }: { active: FunctionKey; subPhase: string; cycle: number }) {
  const showGoal = active === "tor" && (subPhase === "action" || subPhase === "hold");
  const score = active === "spiel" ? "0 : 0" : active === "tor" ? (showGoal ? "1 : 0" : "0 : 0") : "3 : 1";
  const half = active === "spiel" ? "" : active === "tor" ? "(0:0)" : "(2:1)";

  return (
    <div className="flex h-full flex-col bg-white">
      {/* BFV navy header */}
      <div className="px-3 pt-2.5 pb-2.5 text-white" style={{ backgroundColor: BFV_NAVY }}>
        <div className="flex items-center justify-between">
          <Menu className="h-3 w-3" />
          <MoreHorizontal className="h-3 w-3" />
        </div>
        <div className="mt-1.5 flex items-center justify-center gap-2.5">
          <img src={REFERENCE_CREST} alt="" className="h-6 w-6 rounded-full bg-white object-contain p-0.5" />
          <motion.div
            key={`score-${score}`}
            initial={showGoal && subPhase === "action" ? { scale: 1.4 } : false}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <div className="text-lg font-bold leading-none tabular-nums">{score}</div>
            {half && <div className="mt-0.5 text-[8px] opacity-70">{half}</div>}
          </motion.div>
          <img src={REFERENCE_AWAY_CREST} alt="" className="h-6 w-6 rounded-full bg-white object-contain p-0.5" />
        </div>
        <div className="mt-1.5 flex justify-between text-[9px] font-medium leading-tight">
          <span>{REFERENCE_CLUB}</span>
          <span>{REFERENCE_OPPONENT}</span>
        </div>
        {/* tab bar */}
        <div className="mt-2 flex justify-between gap-1 text-[8px] font-semibold uppercase tracking-wider">
          <span className="opacity-50">Info</span>
          <span className="opacity-50">Tab.</span>
          <span className="opacity-50">Verl.</span>
          <span className="border-b border-white pb-0.5">Live</span>
        </div>
      </div>

      {/* Event list */}
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence>
          {active === "tor" && subPhase === "intro" && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="absolute left-0 right-0 top-1 z-10 flex flex-col items-center text-[9px] text-slate-400"
            >
              <span>↓ Ziehen zum Aktualisieren</span>
              <Loader2 className="mt-1 h-3 w-3 animate-spin" style={{ color: TF_BLUE }} />
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          animate={{ y: active === "tor" && subPhase === "intro" ? 14 : 0 }}
          transition={{ type: "spring", stiffness: 220, damping: 22 }}
          className="h-full px-2 pt-2"
        >
          {active === "spiel" && (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Anpfiff in</p>
              <p className="mt-1.5 text-base font-bold text-slate-700">1 Tag</p>
              <p className="mt-1 text-[10px] text-slate-400">Sa · 15:00 Uhr</p>
            </div>
          )}

          {active === "tor" && (
            <div className="divide-y divide-slate-100">
              <AnimatePresence>
                {showGoal && (
                  <motion.div
                    key={`goal-${cycle}`}
                    initial={{ opacity: 0, y: -8, backgroundColor: "rgba(37,99,235,0.30)" }}
                    animate={{
                      opacity: 1,
                      y: 0,
                      backgroundColor: ["rgba(37,99,235,0.30)", "rgba(37,99,235,0.10)", "rgba(37,99,235,0)"],
                    }}
                    transition={{ duration: 1.4 }}
                    className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5 rounded px-1.5 py-2.5 text-[10px] leading-snug"
                  >
                    <span className="font-semibold text-slate-800">M. Müller 72'</span>
                    <span className="font-mono text-xs font-bold" style={{ color: TF_BLUE }}>1:0</span>
                    <span />
                  </motion.div>
                )}
              </AnimatePresence>
              <TickerRow left="" center="0:0" right="Anpfiff 1'" />
            </div>
          )}

          {active === "ergebnis" && (
            <div className="divide-y divide-slate-100">
              <div className="rounded px-1 py-1.5 text-center text-[9px] font-bold uppercase tracking-wider text-slate-500">
                Abpfiff
              </div>
              <TickerRow left="M. Müller 78'" center="3:1" right="" />
              <TickerRow left="M. Müller 45'" center="2:1" right="" />
              <TickerRow left="" center="1:1" right="T. Beispiel 44'" />
              <TickerRow left="M. Müller 7'" center="1:0" right="" />
            </div>
          )}

        </motion.div>
      </div>
    </div>
  );
}

function TickerRow({ left, center, right }: { left: string; center: string; right: string }) {
  return (
    <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-1.5 px-1.5 py-2.5 text-[10px] leading-snug">
      <span className="break-words text-slate-700">{left}</span>
      <span className="font-mono text-xs font-bold text-slate-900">{center}</span>
      <span className="break-words text-right text-slate-700">{right}</span>
    </div>
  );
}

/* ───────────────── Brain Panel ───────────────── */

const PHASE_LABELS = ["SPIELANKÜNDIGUNG", "TORALARM", "ENDERGEBNIS"];
// Three synapse positions inside the brain SVG (viewBox 0 0 100 90)
const SYNAPSES: Array<{ cx: number; cy: number }> = [
  { cx: 34, cy: 40 },
  { cx: 50, cy: 30 },
  { cx: 66, cy: 44 },
];

function BrainPanel({ activeIdx, subPhase, cycle }: { activeIdx: number; subPhase: string; cycle: number }) {
  return (
    <div className="relative flex flex-col items-center justify-center">
      {/* Flow lines */}
      <svg className="pointer-events-none absolute -left-4 top-1/2 hidden h-px w-4 -translate-y-1/2 md:block" viewBox="0 0 16 1">
        <line x1="0" y1="0.5" x2="16" y2="0.5" stroke="hsl(220 14% 88%)" strokeWidth="1" />
      </svg>
      <svg className="pointer-events-none absolute -right-4 top-1/2 hidden h-px w-4 -translate-y-1/2 md:block" viewBox="0 0 16 1">
        <line x1="0" y1="0.5" x2="16" y2="0.5" stroke="hsl(220 14% 88%)" strokeWidth="1" />
      </svg>
      {/* Traveling dots */}
      <AnimatePresence>
        {subPhase === "action" && (
          <>
            <motion.span
              key={`in-${cycle}`}
              initial={{ x: -40, opacity: 0 }}
              animate={{ x: 0, opacity: [0, 1, 1, 0] }}
              transition={{ duration: 1, ease: "easeInOut" }}
              className="pointer-events-none absolute left-0 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full"
              style={{ backgroundColor: TF_BLUE, boxShadow: `0 0 8px ${TF_BLUE}` }}
            />
            <motion.span
              key={`out-${cycle}`}
              initial={{ x: 0, opacity: 0 }}
              animate={{ x: 40, opacity: [0, 1, 1, 0] }}
              transition={{ duration: 1, delay: 0.7, ease: "easeInOut" }}
              className="pointer-events-none absolute right-0 top-1/2 h-1.5 w-1.5 -translate-y-1/2 rounded-full"
              style={{ backgroundColor: TF_BLUE, boxShadow: `0 0 8px ${TF_BLUE}` }}
            />
          </>
        )}
      </AnimatePresence>

      {/* Brain SVG */}
      <motion.div
        animate={subPhase === "action" ? { scale: [1, 1.04, 1] } : { scale: 1 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      >
        <svg viewBox="0 0 100 90" className="h-36 w-36 md:h-40 md:w-40">
          <path
            d="M30 22 C18 24 13 36 19 46 C13 54 19 66 31 67 C35 75 47 76 50 70 C53 76 65 75 69 67 C81 66 87 54 81 46 C87 36 82 24 70 22 C66 14 54 14 50 20 C46 14 34 14 30 22 Z"
            fill="none"
            stroke="hsl(220 14% 30%)"
            strokeWidth="2.1"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M50 20 L50 70"
            stroke="hsl(220 14% 72%)"
            strokeWidth="1.2"
            strokeDasharray="2 2"
            strokeLinecap="round"
          />
          {SYNAPSES.map((n, i) => {
            const isActive = i === activeIdx;
            return (
              <g key={i}>
                {isActive && (
                  <motion.circle
                    key={`r-${cycle}-${i}`}
                    cx={n.cx}
                    cy={n.cy}
                    r="4"
                    fill="none"
                    stroke={TF_BLUE}
                    strokeWidth="1.4"
                    initial={{ opacity: 0.6, r: 4 }}
                    animate={{ opacity: 0, r: 14 }}
                    transition={{ duration: 1.6, ease: "easeOut" }}
                  />
                )}
                <motion.circle
                  cx={n.cx}
                  cy={n.cy}
                  r="3.6"
                  fill={isActive ? TF_BLUE : "hsl(220 14% 78%)"}
                  animate={isActive ? { opacity: [0.6, 1, 0.6] } : { opacity: 0.75 }}
                  transition={{ duration: 1.4, repeat: isActive ? Infinity : 0 }}
                />
              </g>
            );
          })}
        </svg>
      </motion.div>

      {/* Active label */}
      <div className="mt-3 h-4 w-full text-center">
        <AnimatePresence mode="wait">
          <motion.p
            key={PHASE_LABELS[activeIdx]}
            initial={{ opacity: 0, y: 3 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -3 }}
            transition={{ duration: 0.4 }}
            className="text-xs font-bold uppercase tracking-wide"
            style={{ color: TF_BLUE }}
          >
            {PHASE_LABELS[activeIdx]}
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Wordmark */}
      <div className="mt-2 flex items-center gap-1.5">
        <span className="text-[9px] font-semibold uppercase tracking-[0.18em] text-slate-500">Torflash</span>
        <motion.span
          animate={{ opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.2, repeat: Infinity }}
          className="h-1 w-1 rounded-full"
          style={{ backgroundColor: TF_BLUE }}
        />
      </div>

      {/* Phase indicator dots */}
      <div className="mt-2 flex items-center gap-1.5">
        {[0, 1, 2].map((i) => {
          const isActive = i === activeIdx;
          return (
            <motion.span
              key={i}
              animate={isActive ? { scale: [1, 1.4, 1] } : { scale: 1 }}
              transition={{ duration: 0.45 }}
              className="h-1.5 w-1.5 rounded-full"
              style={{ backgroundColor: isActive ? TF_BLUE : "hsl(220 14% 85%)" }}
            />
          );
        })}
      </div>
    </div>
  );
}
